import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { useModal } from '../../../../../context/ModalContext';
import { getAttachmentIcon } from '../../../../../utils/fileUtils';
import useSendTypingIndicator from '../../../../../hooks/useSendTypingIndicator';
import AttachmentModal from './AttachmentModal';
import './MessageInput.css';
import './Messages.css'; // Import for compose-reply-preview styles

const MessageInput = forwardRef(({
  roomId,
  disabled = false,
  message = '',
  placeholder = 'Type a message...',
  replyingTo = null,
  editingMessage = null,
  onCancelReply = () => {},
  onCancelEdit = () => {},
  onSetMessage = (_messageText) => {},
  onSendMessage = () => {},
  onTriggerFileSelect = () => {},
}, ref) => {
  const { onMessageSent, onTextChange } = useSendTypingIndicator(roomId);
  const { showComponent, closeModal } = useModal();

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onMessageSent();
      onSetMessage(''); // Clear immediately
      onSendMessage();
    }
  };

  const onChange = (e) => {
    const newValue = e.target.value;
    onSetMessage(newValue);

    // Handle typing indicators
    onTextChange(newValue);
  };

  const handleSendClick = () => {
    onMessageSent();
    onSetMessage(''); // Clear immediately
    onSendMessage();
  };

  const handleAttachFile = () => {
    closeModal();
    onTriggerFileSelect();
  };

  const handleAttachmentClick = () => {
    if (disabled) return;

    showComponent(
      <AttachmentModal
        onAttachFileClick={handleAttachFile}
      />,
      {
        title: 'Add Attachment',
        size: 'medium',
        closable: true,
      },
    );
  };

  const renderReplyPreview = () => {
    if (!replyingTo) return null;

    // Special compact layout for attachment replies
    if (replyingTo.isAttachment) {
      // Use the thumbnail if it's an image, otherwise get the generic icon
      const showThumbnail = replyingTo.msgtype === 'm.image' && replyingTo.thumbnailUrl;
      const iconSrc = showThumbnail
        ? replyingTo.thumbnailUrl
        : getAttachmentIcon(replyingTo.msgtype, replyingTo.filename);

      return (
        <div className="compose-reply-preview attachment-reply">
          {/* Use 'attachment-reply-thumbnail' class for proper styling of image previews */}
          <img src={iconSrc} alt="attachment icon" className={showThumbnail ? 'attachment-reply-thumbnail' : 'attachment-reply-icon'} />
          <div className="compose-reply-preview-content">
            <div className="compose-reply-preview-header">
              <span className="compose-reply-preview-label">Replying to {replyingTo.senderName}</span>
              <button className="compose-reply-preview-close" onClick={onCancelReply}>
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="compose-reply-preview-message attachment-fileinfo">
              {replyingTo.filename} ({(replyingTo.size)})
            </div>
          </div>
        </div>
      );
    }

    // Default layout for text replies
    return (
      <div className="compose-reply-preview">
        <div className="compose-reply-preview-content">
          <div className="compose-reply-preview-header">
            <span className="compose-reply-preview-label">Replying to {replyingTo.senderName}</span>
            <button className="compose-reply-preview-close" onClick={onCancelReply}>
              <i className="fa fa-times" />
            </button>
          </div>
          <div className="compose-reply-preview-message">
            {replyingTo.content}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="message-input-container">
      {renderReplyPreview()}
      {editingMessage && (
        <div className="compose-reply-preview">
          <div className="compose-reply-preview-content">
            <div className="compose-reply-preview-header">
              <span className="compose-reply-preview-label">Editing message</span>
              <button className="compose-reply-preview-close" onClick={onCancelEdit}>
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="compose-reply-preview-message">
              Original: {editingMessage.originalContent}
            </div>
          </div>
        </div>
      )}
      <div className="message-input-wrapper">
        <button 
          className="message-input-attachment-button"
          onClick={handleAttachmentClick}
          disabled={disabled}
        >
          <span></span>
        </button>
        <textarea
          ref={ref}
          className="message-input"
          placeholder={placeholder}
          disabled={disabled}
          value={message}
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
        <button 
          className="message-send-button"
          onClick={handleSendClick}
          disabled={disabled || !message.trim()}
          type="button"
        >
          <i className="fa fa-paper-plane" />
        </button>
      </div>
    </div>
  );
});

MessageInput.displayName = 'MessageInput';

MessageInput.propTypes = {
  roomId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  message: PropTypes.string,
  placeholder: PropTypes.string,
  replyingTo: PropTypes.shape({
    senderName: PropTypes.string,
    content: PropTypes.string,
  }),
  editingMessage: PropTypes.shape({
    id: PropTypes.string,
    originalContent: PropTypes.string,
    originalEvent: PropTypes.object,
  }),
  onCancelReply: PropTypes.func,
  onCancelEdit: PropTypes.func,
  onSetMessage: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onTriggerFileSelect: PropTypes.func,
};

export default MessageInput;
